import { Heart, Loader2, Lock, MessageCircle, Repeat2, Settings2 } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import type { TomboloState } from '@/hooks/useTombolo';

export function StepRules({ state }: { state: TomboloState }) {
  const { settings, setSettings, setStep, runFetch, isFetching, fetchProgress, fetchError, noteIds, relayUrls, isLocked } = state;

  const anyEnabled = settings.likes || settings.reposts || settings.comments;

  const rows = [
    {
      key: 'likes' as const,
      icon: Heart,
      title: 'Likes count',
      description: 'People who liked or reacted to a note get a ticket.',
    },
    {
      key: 'reposts' as const,
      icon: Repeat2,
      title: 'Reposts count',
      description: 'People who reposted a note get a ticket.',
    },
    {
      key: 'comments' as const,
      icon: MessageCircle,
      title: 'Comments count',
      description: 'People who replied or commented on a note get a ticket.',
    },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Settings2 className="h-5 w-5 text-primary" aria-hidden />
          What counts as an entry?
        </CardTitle>
        <CardDescription className="text-base">
          Choose which actions give people a ticket. No matter what they do,
          each person only ever gets <strong>one ticket</strong>.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-3">
          {rows.map(({ key, icon: Icon, title, description }) => (
            <div key={key} className="flex items-center justify-between gap-4 rounded-lg border p-4">
              <div className="flex items-start gap-3">
                <Icon className="h-5 w-5 text-muted-foreground mt-0.5 shrink-0" aria-hidden />
                <div>
                  <Label htmlFor={`switch-${key}`} className="text-base font-medium cursor-pointer">
                    {title}
                  </Label>
                  <p className="text-sm text-muted-foreground">{description}</p>
                </div>
              </div>
              <Switch
                id={`switch-${key}`}
                checked={settings[key]}
                disabled={isLocked || isFetching}
                onCheckedChange={(checked) => setSettings({ ...settings, [key]: checked })}
              />
            </div>
          ))}

          {/* Always-on rule */}
          <div className="flex items-center justify-between gap-4 rounded-lg border border-dashed bg-muted/40 p-4">
            <div className="flex items-start gap-3">
              <Lock className="h-5 w-5 text-muted-foreground mt-0.5 shrink-0" aria-hidden />
              <div>
                <p className="text-base font-medium">One ticket per person</p>
                <p className="text-sm text-muted-foreground">
                  Always on. Liking, reposting and commenting on many notes still means just one ticket.
                </p>
              </div>
            </div>
            <Switch checked disabled aria-label="One ticket per person (always on)" />
          </div>
        </div>

        <div className="rounded-lg bg-muted/40 border p-4 text-sm text-muted-foreground">
          <p>
            We'll scan <strong className="text-foreground">{noteIds.length} note{noteIds.length === 1 ? '' : 's'}</strong>{' '}
            across <strong className="text-foreground">{relayUrls.length} relay{relayUrls.length === 1 ? '' : 's'}</strong>:
          </p>
          <ul className="mt-1 font-mono text-xs space-y-0.5">
            {relayUrls.map((url) => <li key={url}>{url}</li>)}
          </ul>
        </div>

        {fetchError && (
          <Alert variant="destructive">
            <AlertTitle>Fetching failed</AlertTitle>
            <AlertDescription>{fetchError}</AlertDescription>
          </Alert>
        )}

        <div className="flex flex-col-reverse sm:flex-row justify-between gap-3">
          <Button variant="outline" onClick={() => setStep('notes')} disabled={isFetching}>
            Back
          </Button>
          <Button size="lg" onClick={runFetch} disabled={!anyEnabled || isFetching || noteIds.length === 0}>
            {isFetching ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" aria-hidden />
                {fetchProgress || 'Looking for people…'}
              </>
            ) : (
              'Find the people'
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
